import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { transactionTemplatesService } from '../services/transactionTemplates.service';
import { conditionLabels, countTemplate, partyRoleLabel, sideLabel } from '../utils/transactionTemplates';
// Template pages use the Transaction detail layout (sidebar + main area), per spec.
import './TransactionDetailPage.css';
import './TemplateDetailPage.css';

const DocumentsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
);
const PartiesIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
);

const ItemRow = ({ item }) => (
  <li className="tpl-item-row">
    <div className="tpl-item-main">
      <span className="tpl-item-name">{item.name}</span>
      {conditionLabels(item.condition).map((label) => (
        <span key={label} className="tpl-item-condition">{label}</span>
      ))}
    </div>
    <div className="tpl-item-tags">
      <span className={`tpl-tag ${item.mandatory ? 'tpl-tag--mandatory' : 'tpl-tag--optional'}`}>
        {item.mandatory ? 'Mandatory' : 'Optional'}
      </span>
      {/* A template holds no files: a form item is added as a form, an upload item waits for the agent's file. */}
      {item.type === 'FORM' ? (
        <span className="tpl-tag tpl-tag--type">Form · {item.form?.code}</span>
      ) : (
        <span className="tpl-tag tpl-tag--upload">⬆ Pending upload</span>
      )}
    </div>
  </li>
);

const TemplateDetailPage = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState('documents');
  const [isMine, setIsMine] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setNotFound(false);
    transactionTemplatesService.getTemplate(templateId)
      .then((t) => { if (!cancelled) setTemplate(t); })
      .catch((error) => {
        console.error('[TemplateDetailPage] Failed to load template', templateId, error);
        if (!cancelled) setNotFound(true);
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    // Only the owner may edit — inherited (Brokerage / Broker) templates are read-only.
    transactionTemplatesService.getPersonalTemplates()
      .then((mine) => { if (!cancelled) setIsMine(mine.some((t) => t.id === templateId)); })
      .catch(() => { if (!cancelled) setIsMine(false); });
    return () => { cancelled = true; };
  }, [templateId]);

  const handleBack = () => navigate('/transactions?view=templates');

  if (isLoading || notFound || !template) {
    return (
      <div className="txn-detail-container">
        <div className="txn-layout-wrapper">
          <div className="txn-layout-container">
            <div className="txn-main-area">
              <div className="tpl-detail-message">
                {isLoading ? 'Loading template…' : "This template couldn't be found."}
                {!isLoading && (
                  <button className="txn-back-button" onClick={handleBack} style={{ marginTop: '20px' }}>
                    ← Back to Templates
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const counts = countTemplate(template);
  const isPersonal = !!template.ownerUserId;
  const itemsByFolder = (folder) => template.items.filter((i) => i.folder === folder.slug);

  return (
    <div className="txn-detail-container">
      <div className="txn-layout-wrapper">
      <div className="txn-layout-container">
        <div className="txn-sidebar">
          <div className="txn-sidebar-header">
            <button className="txn-back-button-small" onClick={handleBack}>
              <span>←</span>
              <span>Back</span>
            </button>
            <div className="txn-sidebar-title">{template.name}</div>
            <div className="txn-sidebar-address">
              {isMine ? 'My template' : isPersonal ? (location.state?.sourceLabel || 'Shared template') : 'Rialty template'}
              {' · '}{template.state} · {sideLabel(template.side)}
            </div>
            <div className="txn-sidebar-status-row">
              {isMine ? (
                <button type="button" className="tpl-edit-btn" onClick={() => navigate(`/templates/${template.id}/edit`)}>
                  Edit template
                </button>
              ) : (
                <span className="tpl-readonly-badge">🔒 Read-only</span>
              )}
            </div>
          </div>

          <div className="txn-sidebar-nav">
            <div
              className={`txn-sidebar-nav-item ${activeTab === 'documents' ? 'active' : ''}`}
              onClick={() => setActiveTab('documents')}
            >
              <div className="txn-sidebar-nav-label"><DocumentsIcon /><span>Documents</span></div>
              <div className="txn-sidebar-nav-count">{counts.documentCount}</div>
            </div>
            <div
              className={`txn-sidebar-nav-item ${activeTab === 'parties' ? 'active' : ''}`}
              onClick={() => setActiveTab('parties')}
            >
              <div className="txn-sidebar-nav-label"><PartiesIcon /><span>Parties</span></div>
              <div className="txn-sidebar-nav-count">{counts.partyCount}</div>
            </div>
          </div>
        </div>

        <div className="txn-main-area">
          <div className="tpl-detail-panel">
            {activeTab === 'documents' && (
              <>
                <h2 className="tpl-detail-title">Documents</h2>
                <p className="tpl-detail-subtitle">
                  These checklist items are added to the transaction. Each one is an entry, not a file — forms are added automatically, and upload items wait for the agent's file.
                </p>
                {template.folders.map((folder) => {
                  const items = itemsByFolder(folder);
                  return (
                    <section key={folder.slug} className="tpl-folder">
                      <h3 className="tpl-folder-name">
                        <span aria-hidden="true">📁</span> {folder.name}
                        <span className="tpl-folder-count">{items.length}</span>
                      </h3>
                      <ul className="tpl-item-list">
                        {items.map((item) => <ItemRow key={item.key} item={item} />)}
                      </ul>
                    </section>
                  );
                })}
              </>
            )}

            {activeTab === 'parties' && (
              <>
                <h2 className="tpl-detail-title">Parties</h2>
                <p className="tpl-detail-subtitle">
                  These roles are added to the transaction as "To be invited". The agent adds contact details and sends
                  the invites — nobody is invited automatically.
                </p>
                <ul className="tpl-item-list">
                  {template.parties.map((role) => (
                    <li key={role} className="tpl-item-row">
                      <span className="tpl-item-name">{partyRoleLabel(role)}</span>
                      <span className="tpl-tag tpl-tag--type">To be invited</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default TemplateDetailPage;
